import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Form,
  Alert,
  Spinner,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import {
  Prescription,
  ArrowLeft,
  CheckCircle,
  InfoCircle,
  PersonCircle,
  Telephone,
  Envelope,
  Droplet,
  Calendar,
  Shield,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

// === Données des patients (identique à MyPatients et PatientMedicalRecord) ===
const allPatients = [
  {
    id: 'CNAMGS-123456',
    name: 'Jean Dupont',
    birthDate: '15/05/1985',
    lastVisit: '2025-03-12',
    history: 'Hypertension, allergie aux pénicillines',
    phone: '+241 01 23 45 67',
    email: 'jean.dupont@email.ga',
    bloodType: 'A+',
    photo: 'https://randomuser.me/api/portraits/men/1.jpg',
  },
  {
    id: 'CNAMGS-789012',
    name: 'Marie Nzogho',
    birthDate: '23/09/1990',
    lastVisit: '2025-03-10',
    history: 'Asthme, diabète type 2',
    phone: '+241 01 23 45 68',
    email: 'marie.nzogho@email.ga',
    bloodType: 'O+',
    photo: 'https://randomuser.me/api/portraits/women/2.jpg',
  },
  {
    id: 'CNAMGS-345678',
    name: 'Pierre Mba',
    birthDate: '10/12/1978',
    lastVisit: '2025-03-05',
    history: 'Aucun antécédent notable',
    phone: '+241 01 23 45 69',
    email: 'pierre.mba@email.ga',
    bloodType: 'B+',
    photo: 'https://randomuser.me/api/portraits/men/3.jpg',
  },
  {
    id: 'CNAMGS-111222',
    name: 'Sophie Nguema',
    birthDate: '02/07/1995',
    lastVisit: '2025-03-15',
    history: 'Allergie aux pollens',
    phone: '+241 01 23 45 70',
    email: 'sophie.nguema@email.ga',
    bloodType: 'A-',
    photo: 'https://randomuser.me/api/portraits/women/4.jpg',
  },
  {
    id: 'CNAMGS-333444',
    name: 'Paul Obiang',
    birthDate: '18/11/1982',
    lastVisit: '2025-03-08',
    history: 'Diabète de type 2',
    phone: '+241 01 23 45 71',
    email: 'paul.obiang@email.ga',
    bloodType: 'AB+',
    photo: 'https://randomuser.me/api/portraits/men/5.jpg',
  },
  {
    id: 'CNAMGS-555666',
    name: 'Claire Moussavou',
    birthDate: '30/01/1989',
    lastVisit: '2025-03-14',
    history: 'Aucun antécédent',
    phone: '+241 01 23 45 72',
    email: 'claire.moussavou@email.ga',
    bloodType: 'O-',
    photo: 'https://randomuser.me/api/portraits/women/6.jpg',
  },
  {
    id: 'CNAMGS-777888',
    name: 'David Ngoma',
    birthDate: '05/04/1975',
    lastVisit: '2025-03-02',
    history: 'Hypertension',
    phone: '+241 01 23 45 73',
    email: 'david.ngoma@email.ga',
    bloodType: 'B-',
    photo: 'https://randomuser.me/api/portraits/men/7.jpg',
  },
  {
    id: 'CNAMGS-999000',
    name: 'Anne Mintogo',
    birthDate: '22/12/1992',
    lastVisit: '2025-03-09',
    history: 'Aucun antécédent',
    phone: '+241 01 23 45 74',
    email: 'anne.mintogo@email.ga',
    bloodType: 'A+',
    photo: 'https://randomuser.me/api/portraits/women/8.jpg',
  },
];

const PrescriptionForm = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addPrescription } = useData();

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [formData, setFormData] = useState({
    medicines: '',
    dosage: '',
    duration: '',
    instructions: '',
  });

  const patient = allPatients.find(p => p.id === patientId);
  if (!patient) {
    return (
      <div className="container mt-5 text-center">
        <h3>Patient introuvable</h3>
        <Button variant="primary" onClick={() => navigate('/doctor/search')}>
          Retour à la recherche
        </Button>
      </div>
    );
  }

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.medicines.trim() || !formData.dosage.trim()) {
      showToast('Veuillez remplir les champs obligatoires (médicaments et posologie)', 'warning');
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const newPrescription = {
        id: `P${Date.now()}`,
        patientId: patient.id,
        doctorId: user?.id || 'DOC001',
        doctorName: user?.name || 'Dr. Nzogho',
        date: new Date().toISOString().split('T')[0],
        medicines: [{ name: formData.medicines, dosage: formData.dosage, quantity: 1, instructions: formData.instructions }],
        status: 'pending',
        duration: formData.duration,
      };
      if (addPrescription) addPrescription(newPrescription);
      setLoading(false);
      showToast('Prescription envoyée avec succès à la pharmacie', 'success');
      setTimeout(() => navigate('/doctor/search'), 2000);
    }, 800);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700&display=swap" rel="stylesheet" />

      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <Button
            variant="link"
            className="p-0 mb-2 d-flex align-items-center text-decoration-none"
            onClick={() => navigate('/doctor/search')}
            style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6c757d' }}
          >
            <ArrowLeft size={14} className="me-1" /> Retour à la recherche
          </Button>
          <div className="d-flex align-items-center gap-2">
            <div className="bg-success bg-opacity-10 rounded-circle p-2">
              <Prescription size={20} className="text-success" />
            </div>
            <h4 className="main-title mb-0" style={{ fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.3px' }}>
              Prescription électronique
            </h4>
          </div>
          <p className="text-muted mt-1 mb-0" style={{ fontSize: '0.85rem' }}>
            Remplissez les détails de la prescription pour {patient.name}
          </p>
        </div>

        {/* Informations patient (en-tête) */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
          <div className="p-3" style={{ background: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)' }}>
            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-white" style={{ fontSize: '0.9rem', fontWeight: 500 }}>
              <PersonCircle size={16} />
              Informations du patient
            </h5>
          </div>
          <Card.Body className="p-3">
            <Row className="align-items-center">
              <Col xs={12} md={3} className="text-center mb-3 mb-md-0">
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto', border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <img src={patient.photo} alt={patient.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <Badge bg="success" className="mt-2 rounded-pill px-2 py-1" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                  <CheckCircle size={10} className="me-1" /> Actif
                </Badge>
              </Col>
              <Col xs={12} md={9}>
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>Nom complet</small>
                      <strong style={{ fontSize: '0.85rem', fontWeight: 600 }}>{patient.name}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>N° d'assuré</small>
                      <strong style={{ fontSize: '0.85rem', fontWeight: 600 }}>{patient.id}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>Date de naissance</small>
                      <strong style={{ fontSize: '0.85rem', fontWeight: 600 }}>{patient.birthDate}</strong>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>Dernière visite</small>
                      <strong style={{ fontSize: '0.85rem', fontWeight: 600 }}>{formatDate(patient.lastVisit)}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>Groupe sanguin</small>
                      <strong style={{ fontSize: '0.85rem', fontWeight: 600 }}><Droplet size={12} className="text-danger me-1" /> {patient.bloodType}</strong>
                    </div>
                    <div className="mb-2">
                      <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: 500 }}>Contact</small>
                      <div style={{ fontSize: '0.8rem' }}><Telephone size={12} className="me-1" /> {patient.phone}</div>
                      <div style={{ fontSize: '0.8rem' }}><Envelope size={12} className="me-1" /> {patient.email}</div>
                    </div>
                  </Col>
                </Row>
                <Alert variant="info" className="mt-2 mb-0 p-2" style={{ background: '#e8f4fd', borderLeft: '3px solid #0d6efd', fontSize: '0.75rem' }}>
                  <Shield size={12} className="me-1" />
                  <strong>Antécédents :</strong> {patient.history}
                </Alert>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Formulaire de prescription (inchangé) */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Médicament(s) <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Ex: Paracétamol 500mg, Amoxicilline 250mg"
                  value={formData.medicines}
                  onChange={(e) => setFormData({ ...formData, medicines: e.target.value })}
                  className="border-0 bg-light"
                  required
                  style={{ fontSize: '0.85rem' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Posologie <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: 2 comprimés par jour pendant 5 jours"
                  value={formData.dosage}
                  onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                  className="border-0 bg-light"
                  required
                  style={{ fontSize: '0.85rem' }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Durée du traitement</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: 7 jours"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="border-0 bg-light"
                  style={{ fontSize: '0.85rem' }}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Instructions complémentaires</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Ex: À prendre après les repas, éviter l'alcool..."
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  className="border-0 bg-light"
                  style={{ fontSize: '0.85rem' }}
                />
              </Form.Group>

              <div className="d-flex gap-3">
                <Button
                  type="submit"
                  variant="success"
                  className="rounded-pill px-4 py-2"
                  disabled={loading}
                  style={{ fontSize: '0.85rem', fontWeight: 500 }}
                >
                  {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
                  {loading ? 'Envoi...' : 'Envoyer la prescription'}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill px-4 py-2"
                  onClick={() => navigate('/doctor/search')}
                  style={{ fontSize: '0.85rem', fontWeight: 500 }}
                >
                  Annuler
                </Button>
              </div>
            </Form>

            <Alert variant="info" className="mt-4 p-2" style={{ fontSize: '0.75rem', background: '#e8f4fd' }}>
              <InfoCircle size={14} className="me-1" />
              La prescription sera automatiquement transmise à la pharmacie choisie par le patient.
              Vous pourrez suivre son statut dans la liste des prescriptions.
            </Alert>
          </Card.Body>
        </Card>

        <style>{`
          .card {
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }
          .card:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 20px -8px rgba(0, 0, 0, 0.15) !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default PrescriptionForm;